<!--
  Выбирает сотрудника компании через общий store.
  autoload=false отключает только начальную загрузку списка; поиск и выбранное значение продолжают работать.
-->
<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :disabled="disabled"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />
</template>

<script>
import { mapState } from "pinia";

import { useCompanyUserStore } from "@/stores/companyUserStore";

export default {
  name: "CompanyUserSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите пользователя",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить нового пользователя",
    },
    noResultsText: {
      type: String,
      default: "Пользователи не найдены",
    },
    autoload: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["update:modelValue", "change", "button-click"],

  data() {
    return {
      preselectedOption: null,
      isSearchActive: false,
    };
  },

  computed: {
    ...mapState(useCompanyUserStore, [
      "companyUsers",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.companyUsers.map((companyUser) => ({
        value: companyUser.user_id,
        label: companyUser.selector_name,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useCompanyUserStore();
      await store.fetchCompanyUsers({ q: { search: searchData.query } });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useCompanyUserStore();
      await store.fetchCompanyUsers();
    },

    async handleScrollEnd() {
      const store = useCompanyUserStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.dataIsLoading
      ) {
        await store.loadMoreCompanyUsers();
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useCompanyUserStore();
      const exists = store.companyUsers.find(
        (u) => String(u.user_id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        await store.ensureCompanyUser(this.modelValue);
        const found = store.companyUsers.find(
          (u) => String(u.user_id) === String(this.modelValue)
        );
        if (found) {
          this.preselectedOption = {
            value: found.user_id,
            label: found.selector_name,
          };
          store.companyUsers = store.companyUsers.filter(
            (u) => String(u.user_id) !== String(this.modelValue)
          );
        }
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного пользователя:",
          error
        );
      }
    },
  },

  async mounted() {
    const store = useCompanyUserStore();
    if (this.autoload) await store.fetchCompanyUsers();
    await this.fetchPreselectedOption();
  },
};
</script>
